import React, { forwardRef, useCallback, useRef, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Theme,
  Typography,
  Skeleton,
} from '@mui/material';
import classNames from 'classnames';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import makeStyles from '@mui/styles/makeStyles';
import pluralize from 'pluralize';
import Search from '@mui/icons-material/Search';
import {
  ActionPlanContextProvider,
  BlockingActionPlanContextProvider,
} from './ActionPlanContext';
import ActionPlanWizard, {
  ActionPlanWizardProps,
  ActionPlanWizardState,
  ActionPlanWizardStep,
} from './ActionPlanWizard';
import { buildInitialMention, buildSearcherMentions } from '../mentions/helper';
import { ConversableType } from '../../enums/Conversations';
import {
  ConversationThreadSchema,
  RoomSchema,
} from '../../definitions/schemas';
import { InsertMentiontype } from '../Input/RichTextEditor';
import { Properties, Referrers } from '../../enums/Analytics';
import { roomForMentionsFields } from '../../graphql/Rooms/RoomFields';
import { useI18n } from '../../lib/hook/useI18n';
import Drawer from '../../theme/components/Drawer/Drawer';
import InlineConversationInput from '../Conversation/InlineInput';
import Link from '../Link';
import MentionInvite, { MentionInviteMethods } from '../mentions/MentionInvite';
import RoomsInterface from '../../graphql/Rooms/RoomsInterface';
import useResource from '../../lib/hook/useResource';
import { TaskDetailsTab } from './Steps/TaskDetailsEnum';

const useStyles = makeStyles((theme: Theme) => ({
  backButton: {
    marginLeft: theme.spacing(-2),
  },
  textfield: {
    backgroundColor: theme.palette.background.paper,
  },
}));

const MentionInviteComponent = forwardRef(MentionInvite);

type PickedActionPlanWizardProps = Omit<
  ActionPlanWizardProps,
  'active' | 'children'
>;

export interface ActionPlanEditorProps extends PickedActionPlanWizardProps {
  allowNameChange?: boolean;
  disableDelete?: boolean;
  disableNavigationBlock?: boolean;
  drawerHeader?: React.ReactNode;
  hideBackdrop?: boolean;
  isGuestVisitor?: boolean;
  open: boolean;
  roomUUID?: string;
  useBackButtonToClose?: boolean;
}

const ActionPlanEditor: React.FC<ActionPlanEditorProps> = (
  props: ActionPlanEditorProps
): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  const {
    actionPlanLabel,
    allowDiscussion,
    allowNameChange = false,
    contact,
    deal,
    disableDelete = false,
    disableNavigationBlock = false,
    drawerHeader,
    hideBackdrop = false,
    isGuestVisitor = false,
    onClose,
    open,
    roomUUID,
    useBackButtonToClose = false,
    userContactId,
    viewOnly,
    ...actionPlanWizardProps
  } = props;

  const { allowInvitations } = actionPlanWizardProps;

  const [refreshRoomData, setRefreshRoomData] = useState(0);

  const mentionInviteRef = useRef<MentionInviteMethods | null>(null);
  // save conversations and room data as ref since tinymce save their own closure and don't update the context on changes
  const conversationsRef = useRef<ConversationThreadSchema[]>([]);
  const roomRef = useRef<RoomSchema>();

  const [room] = useResource(
    () => loadRoom(),
    null,
    [refreshRoomData],
    !!roomUUID && open
  );

  const initialMention = useCallback(() => buildInitialMention(room), [room]);

  const loadRoom = async () => {
    if (!roomUUID) {
      return;
    }

    const room = await new RoomsInterface(userContactId).getRoom(
      { uuid: roomUUID },
      false,
      roomForMentionsFields
    );
    roomRef.current = room;

    return room;
  };

  const searcherMentions = async (input: string) =>
    buildSearcherMentions(
      input,
      roomRef.current!,
      conversationsRef.current,
      userContactId
    );

  const shouldShowCloseButton = (step: ActionPlanWizardStep) =>
    !useBackButtonToClose &&
    ![
      ActionPlanWizardStep.EditTask,
      ActionPlanWizardStep.ViewTaskDetails,
    ].includes(step);

  const shouldShowBackButton = (step: ActionPlanWizardStep) =>
    useBackButtonToClose ||
    [
      ActionPlanWizardStep.EditTask,
      ActionPlanWizardStep.ViewTaskDetails,
    ].includes(step);

  const handleBack = (state: ActionPlanWizardState) => {
    if (useBackButtonToClose && state.step === ActionPlanWizardStep.ViewTasks) {
      state.onClose();
      return;
    }

    state.onBack();
  };

  const handleActionPlanNameChange = (state: ActionPlanWizardState) => {
    const { name, onUpdateActionPlan } = state;
    return (e: React.FocusEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      if (newName !== name) {
        onUpdateActionPlan(newName);
      }
    };
  };

  const handleTemplatesSearchChange =
    (state: ActionPlanWizardState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      state.setTemplatesSearch(e.target.value);
    };

  const handleContactsInvited = () => {
    setRefreshRoomData(Date.now());
  };

  const onInvite = (input: string, onInsertMention: InsertMentiontype) =>
    mentionInviteRef.current?.onOwnerInvite(input, onInsertMention);

  const renderSelectTemplate = (state: ActionPlanWizardState): JSX.Element => (
    <Grid alignItems="center" container>
      <Typography color="textSecondary" variant="body1">
        Already have one?
      </Typography>
      <Box ml={2}>
        <Link
          onClick={() => state.setStep(ActionPlanWizardStep.SelectTemplate)}
          text="Select a template"
        />
      </Box>
    </Grid>
  );

  const renderStartFromScratch = (
    state: ActionPlanWizardState
  ): JSX.Element => (
    <Grid container spacing={2}>
      <Grid alignItems="center" container item xs={12}>
        <Typography color="textSecondary" variant="body1">
          Can&apos;t find the right template?
        </Typography>
        <Box ml={2}>
          <Link
            onClick={() => state.setStep(ActionPlanWizardStep.CreateActionPlan)}
            text="Create from scratch"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <TextField
          color="secondary"
          fullWidth
          InputProps={{
            className: classes.textfield,
            endAdornment: <Search />,
          }}
          onChange={handleTemplatesSearchChange(state)}
          size="small"
          placeholder="Search"
          value={state.templatesSearch}
          variant="outlined"
        />
      </Grid>
    </Grid>
  );

  const renderSubtitle = (
    state: ActionPlanWizardState
  ): JSX.Element | undefined => {
    const { ready, step } = state;

    if (!ready) {
      return undefined;
    }

    switch (step) {
      case ActionPlanWizardStep.CreateActionPlan:
        return state.hasTemplates ? renderSelectTemplate(state) : undefined;
      case ActionPlanWizardStep.SelectTemplate:
        return renderStartFromScratch(state);
    }
  };

  const renderNameEditor = (state: ActionPlanWizardState): JSX.Element => (
    <Grid alignItems="center" container spacing={1}>
      <Grid item xs={9}>
        <TextField
          color="secondary"
          defaultValue={state.name}
          fullWidth
          onBlur={handleActionPlanNameChange(state)}
          label="Plan name"
          variant="outlined"
        />
      </Grid>
      {!disableDelete && (
        <Grid item xs={3}>
          <Box color="text.disabled">
            <IconButton
              color="inherit"
              onClick={state.onDeleteActionPlan}
              size="large"
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderSkeleton = () => <Skeleton height={32} width="100%" />;

  const renderTaskDetailsTitle = (
    state: ActionPlanWizardState
  ): JSX.Element | undefined => {
    const { activeTaskParent, name, step } = state;

    if (step !== ActionPlanWizardStep.ViewTaskDetails) {
      return undefined;
    }

    let title = `${name}`;
    if (activeTaskParent) {
      title = activeTaskParent.name;
    }

    return (
      <Typography
        color="textSecondary"
        noWrap
        sx={{ ml: 1 }}
        title={title}
        variant="body1"
      >
        {title}
      </Typography>
    );
  };

  const renderTitle = (state: ActionPlanWizardState): React.ReactNode => {
    const { activeTask, loadingTask, ready, step } = state;

    if (!ready || loadingTask) {
      return renderSkeleton();
    }

    const label = pluralize(actionPlanLabel || 'mutual action plan', 1);

    switch (step) {
      case ActionPlanWizardStep.CreateActionPlan:
        return `Create ${label}`;
      case ActionPlanWizardStep.EditTask:
        const type = activeTask?.parentTaskId ? 'subtask' : 'task';
        return t('map.task.details.edit.task', {
          type: t(`map.${type}`),
        });
      case ActionPlanWizardStep.SelectTemplate:
        return 'Select template';
      case ActionPlanWizardStep.ViewTasks:
        return allowNameChange && !viewOnly
          ? renderNameEditor(state)
          : state.name;
    }
  };

  const renderTitleAction = (state: ActionPlanWizardState): React.ReactNode => {
    if (viewOnly || state.step !== ActionPlanWizardStep.ViewTaskDetails) {
      return undefined;
    }

    return state.renderTaskMenu();
  };

  const renderActions = (
    state: ActionPlanWizardState
  ): JSX.Element | undefined => {
    if (
      viewOnly ||
      !allowDiscussion ||
      state.step !== ActionPlanWizardStep.ViewTaskDetails ||
      state.taskDetailsTab !== TaskDetailsTab.Discussion
    ) {
      return undefined;
    }

    return (
      <>
        <InlineConversationInput
          authorId={userContactId}
          buttonLabel={t('map.task.details.conversation.post')}
          conversableId={state.activeTask?.id}
          conversableType={ConversableType.ActionPlanTask}
          mentions={{
            allowInvitations,
            enabled: true,
            initialMention: initialMention(),
            onInvite,
            searcher: searcherMentions,
          }}
          onPublish={state.onCreateConversationThread}
          onTextInputClick={state.resetScroll}
          placeholder={t('map.task.details.conversation.type_message')}
        />
        {!!room && (
          <MentionInviteComponent
            ref={mentionInviteRef}
            actionLabel={t('map.task.details.conversation.invite')}
            contactId={userContactId}
            forceEmailInput
            handleContactsInvited={handleContactsInvited}
            roomId={room?.id}
            title={t('map.task.details.conversation.invite_title')}
            trackingProperties={{
              [Properties.ENTERED_FROM]:
                Referrers.DEAL_ROOMS.MAP_TASK_DISCUSSION_MENTION,
            }}
          />
        )}
      </>
    );
  };

  const renderActionPlanWizard = (): JSX.Element => (
    <ActionPlanWizard
      actionPlanLabel={actionPlanLabel}
      active={open}
      allowDiscussion={allowDiscussion}
      contact={contact}
      conversationsRef={conversationsRef}
      deal={deal}
      onClose={onClose}
      userContactId={userContactId}
      viewOnly={viewOnly}
      {...actionPlanWizardProps}
    >
      {(component: React.ReactNode, state: ActionPlanWizardState) => (
        <Drawer
          actions={renderActions(state)}
          anchor="right"
          backButtonLabel={renderTaskDetailsTitle(state)}
          classes={{
            backButton: classNames({
              [classes.backButton]:
                useBackButtonToClose &&
                state.step === ActionPlanWizardStep.ViewTasks,
            }),
          }}
          contentRef={state.contentRef}
          disableEnforceFocus
          hideBackdrop={hideBackdrop}
          multilineTitle
          onBack={() => handleBack(state)}
          onClose={state.onClose}
          open={open}
          paddingBottom={{ xs: 0.5, md: 1 }}
          PaperProps={{
            style: {
              background:
                state.step === ActionPlanWizardStep.SelectTemplate
                  ? 'var(--background-box-dark)'
                  : undefined,
            },
          }}
          showBackButton={shouldShowBackButton(state.step)}
          showCloseButton={shouldShowCloseButton(state.step)}
          stickyHeader={drawerHeader}
          subtitle={renderSubtitle(state)}
          title={renderTitle(state)}
          titleAction={renderTitleAction(state)}
        >
          {component}
        </Drawer>
      )}
    </ActionPlanWizard>
  );

  const renderActionPlanEditor = (): JSX.Element => {
    if (disableNavigationBlock) {
      return renderActionPlanWizard();
    }

    return (
      <BlockingActionPlanContextProvider>
        {renderActionPlanWizard()}
      </BlockingActionPlanContextProvider>
    );
  };

  return (
    <ActionPlanContextProvider isGuestVisitor={isGuestVisitor}>
      {renderActionPlanEditor()}
    </ActionPlanContextProvider>
  );
};

export default ActionPlanEditor;
